import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  usePartnerProducts,
  deriveCategories,
  partnerCategoryLabel,
} from '@/hooks/usePartnerProducts';
import { Badge, Button, Card, CardTitle } from '@/components/ui';
import { categoryTheme } from '@/lib/categoryTheme';
import { toast } from '@/components/ui/Toast';
import { useCart } from '@/store/cart';
import { formatCOP } from '@/lib/format';

/**
 * StoreCategory — ROUND7 P1 / ROUND8. Listing filtrado por slug de categoría
 * (`/store/category/:slug`). Datos reales desde `public.partner_products`.
 */
export function StoreCategoryPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: products, isLoading, isError } = usePartnerProducts();
  const addProductLine = useCart((s) => s.addProductLine);

  if (isLoading) {
    return (
      <section className="container-fc py-12" aria-busy="true">
        <div className="h-8 w-48 rounded bg-neutral-100" />
        <div className="mt-4 h-5 w-full max-w-md rounded bg-neutral-100" />
      </section>
    );
  }

  const all = products ?? [];
  const inCategory = slug ? all.filter((p) => p.category === slug) : [];
  const categoryExists = inCategory.length > 0;

  if (isError || !slug || !categoryExists) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">
          {isError ? 'No pudimos cargar la categoría' : 'Categoría no encontrada'}
        </h1>
        <p className="mt-2 text-fc-muted">
          {isError
            ? 'Ocurrió un error al cargar esta categoría. Probá recargar la página.'
            : 'La categoría que buscás no existe o no tiene productos. Mirá toda la tienda.'}
        </p>
        <Button className="mt-6" variant="secondary" onClick={() => navigate('/store')}>
          Ver toda la tienda
        </Button>
      </section>
    );
  }

  const categoryName = partnerCategoryLabel(slug);
  const otherCategories = deriveCategories(all).filter((c) => c.slug !== slug);

  return (
    <section className="container-fc py-8 md:py-12">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-fc-muted">
        <Link to="/store" className="hover:text-fc-ink">
          Tienda
        </Link>
        <span aria-hidden> / </span>
        <span className="text-fc-ink">{categoryName}</span>
      </nav>

      <header className="mb-8">
        <h1 className="font-display text-3xl md:text-4xl">{categoryName}</h1>
        <p className="mt-3 max-w-2xl text-base text-fc-muted">
          Productos y servicios de la categoría {categoryName} curados por los partners First Class.
        </p>
      </header>

      <div className="mb-6 flex flex-wrap gap-2">
        <Button as={Link} to="/store" variant="ghost" size="sm">
          ← Toda la tienda
        </Button>
        {otherCategories.map((c) => (
          <Button
            key={c.slug}
            as={Link}
            to={`/store/category/${c.slug}`}
            variant="secondary"
            size="sm"
          >
            {c.label}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {inCategory.map((p) => (
          <Card key={p.id} variant="default" padded>
            <Badge tone={categoryTheme(p.category).tone} className="mb-3">
              {categoryName}
            </Badge>
            <CardTitle>{p.title}</CardTitle>
            <p className="mt-1 text-xs text-fc-muted">{p.partnerName}</p>
            {p.description && (
              <p className="mt-3 line-clamp-3 text-sm text-fc-muted">{p.description}</p>
            )}
            <div className="mt-5 flex items-center justify-between gap-3">
              <span className="text-base font-semibold tabular-nums">
                {formatCOP(p.priceCents)}
              </span>
              <div className="flex gap-2">
                <Button as={Link} to={`/store/${p.id}`} variant="secondary" size="sm">
                  Detalle
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    addProductLine({
                      productId: p.id,
                      partnerId: p.partnerId,
                      partnerName: p.partnerName,
                      title: p.title,
                      unitPriceCents: p.priceCents,
                      quantity: 1,
                    });
                    toast.success({ title: 'Agregado al carrito', description: p.title });
                  }}
                >
                  Agregar
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}
